// packages/web/src/screens/RecordsHarness.tsx — R2: a minimal DEV harness that
// proves the schema-driven record-operations engine end to end through the
// REAL records API. NOT a product screen (R3 wires RecordForm/RecordActions
// into the actual detail screens); this exists so create/edit/delete/
// transition are demonstrably exercised against the live engine on a handful
// of record types (actor_project, actor_payment, actor_audition, …).
//
// Routes it owns (dev-only, hash paths under /_records):
//   /_records                    — the type index (create/browse per type)
//   /_records/new/:type          — create form
//   /_records/:type/:id/edit     — edit form + actions
import { useEffect, useState } from 'preact/hooks';
import { RecordForm } from '../components/RecordForm';
import { RecordActions } from '../components/RecordActions';
import {
  ApiError,
  getRecordTypes,
  getRecordTypeSchema,
  getRecordWithLinks,
  listRecordsByType,
  type RecordRow,
  type RecordTypeSummary,
  type RecordTypeSchema,
  type LinkRow,
} from '../lib/api';
import './RecordsHarness.css';

interface HarnessRoute {
  mode: 'index' | 'new' | 'edit';
  type?: string;
  id?: string;
}

/** Parses a /_records… hash path into the harness sub-route. */
export function parseHarnessRoute(path: string): HarnessRoute {
  const segs = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  // segs[0] === '_records'
  if (segs[1] === 'new' && segs[2]) return { mode: 'new', type: segs[2] };
  if (segs[1] && segs[2] && segs[3] === 'edit') return { mode: 'edit', type: segs[1], id: segs[2] };
  return { mode: 'index' };
}

export function RecordsHarness({
  tenantId,
  path,
  navigate,
}: {
  tenantId: string;
  path: string;
  navigate: (path: string) => void;
}) {
  const route = parseHarnessRoute(path);

  return (
    <div class="rh">
      <div class="rh__bar">
        <button type="button" class="rh__home" onClick={() => navigate('/_records')}>
          ← Records engine (dev)
        </button>
      </div>
      {route.mode === 'index' && <TypeIndex tenantId={tenantId} navigate={navigate} />}
      {route.mode === 'new' && route.type && (
        <CreateView tenantId={tenantId} typeKey={route.type} navigate={navigate} />
      )}
      {route.mode === 'edit' && route.type && route.id && (
        <EditView tenantId={tenantId} typeKey={route.type} id={route.id} navigate={navigate} />
      )}
    </div>
  );
}

function TypeIndex({ tenantId, navigate }: { tenantId: string; navigate: (p: string) => void }) {
  const [types, setTypes] = useState<RecordTypeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openType, setOpenType] = useState<string | null>(null);

  useEffect(() => {
    getRecordTypes(tenantId)
      .then(setTypes)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load types.'));
  }, [tenantId]);

  if (error) return <div class="rh__error" role="alert">{error}</div>;
  if (!types) return <div class="rh__loading">Loading…</div>;

  return (
    <div class="rh__index">
      {types.map((t) => (
        <div class="rh__type" key={t.key}>
          <div class="rh__type-head">
            <div>
              <div class="rh__type-name">{t.labelPlural}</div>
              <div class="rh__type-key">{t.key}{t.hasLifecycle ? ' · lifecycle' : ''}</div>
            </div>
            <div class="rh__type-actions">
              <button type="button" class="rh__btn" onClick={() => navigate(`/_records/new/${t.key}`)}>
                + New
              </button>
              <button
                type="button"
                class="rh__btn rh__btn--ghost"
                onClick={() => setOpenType(openType === t.key ? null : t.key)}
              >
                {openType === t.key ? 'Hide' : 'Browse'}
              </button>
            </div>
          </div>
          {openType === t.key && <RecordList tenantId={tenantId} typeKey={t.key} navigate={navigate} />}
        </div>
      ))}
    </div>
  );
}

function RecordList({ tenantId, typeKey, navigate }: { tenantId: string; typeKey: string; navigate: (p: string) => void }) {
  const [rows, setRows] = useState<RecordRow[] | null>(null);
  useEffect(() => {
    listRecordsByType(tenantId, typeKey).then(setRows).catch(() => setRows([]));
  }, [tenantId, typeKey]);
  if (!rows) return <div class="rh__loading">Loading…</div>;
  if (rows.length === 0) return <div class="rh__hint">No records yet.</div>;
  return (
    <ul class="rh__list">
      {rows.map((r) => (
        <li key={r.id}>
          <button type="button" class="rh__row" onClick={() => navigate(`/_records/${typeKey}/${r.id}/edit`)}>
            <span>{r.display_name || r.id}</span>
            {r.lifecycle_state && <span class="rh__badge">{r.lifecycle_state}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function CreateView({ tenantId, typeKey, navigate }: { tenantId: string; typeKey: string; navigate: (p: string) => void }) {
  return (
    <div class="rh__panel">
      <RecordForm
        tenantId={tenantId}
        typeKey={typeKey}
        onSaved={(rec) => navigate(`/_records/${typeKey}/${rec.id}/edit`)}
        onCancel={() => navigate('/_records')}
      />
    </div>
  );
}

function EditView({ tenantId, typeKey, id, navigate }: { tenantId: string; typeKey: string; id: string; navigate: (p: string) => void }) {
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [existing, setExisting] = useState<{ record: RecordRow; links: LinkRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.all([getRecordTypeSchema(tenantId, typeKey), getRecordWithLinks(tenantId, id)])
      .then(([s, r]) => {
        if (!alive) return;
        setSchema(s);
        setExisting(r);
      })
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not load the record.'));
    return () => {
      alive = false;
    };
  }, [tenantId, typeKey, id, nonce]);

  if (error) return <div class="rh__error" role="alert">{error}</div>;
  if (!schema || !existing) return <div class="rh__loading">Loading…</div>;

  return (
    <div class="rh__panel">
      <RecordForm
        tenantId={tenantId}
        typeKey={typeKey}
        schema={schema}
        existing={existing}
        onSaved={() => setNonce((n) => n + 1)}
        onCancel={() => navigate('/_records')}
      />
      <RecordActions
        tenantId={tenantId}
        schema={schema}
        record={existing.record}
        onChanged={() => setNonce((n) => n + 1)}
        onDeleted={() => navigate('/_records')}
      />
    </div>
  );
}
